import axios from "axios";
import { FormToFilling } from "../types/new-form-filling/types";

export type LocalizacaoPublica = {
  latitude: number;
  longitude: number;
};

export type ConfiguracaoValidadorPublico = {
  ativo: boolean;
  pergunta: string;
  placeholder: string;
  tentativasAntesDeContinuar: number;
  modoConfirmacao?:
    | "redigitar_identificador"
    | "digitar_palavra"
    | "digitar_ciente";
  confirmacaoSemAgendamento?: "redigitar_identificador" | "digitar_ciente";
  palavraConfirmacao?: string;
  textoConfirmacao?: string;
};

export type ChecklistPublico = FormToFilling & {
  validadorAgendamento?: ConfiguracaoValidadorPublico;
};

export type SessaoCheckinFluxo = {
  sessao: string;
  empresa: string;
  idChecklistAnonimo: string;
  tentativas: number;
  tentativasMaximas: number;
  reutilizada: boolean;
  configuracao?: { pergunta?: string; confirmacaoSemAgendamento?: string };
  validacaoAgendamento?: {
    pergunta?: string;
    confirmacaoSemAgendamento?: string;
  };
};

export type AberturaCheckinFluxo = {
  checklist: ChecklistPublico;
  agendamento: Record<string, unknown> | null;
};

export type ValidacaoCheckinFluxo = {
  agendamento: AgendamentoPublico | null;
  tentativas: number;
  tentativasMaximas: number;
  podeContinuarSemAgendamento: boolean;
};

export type AgendamentoPublico = {
  dataPrevista?: number;
  resumoAgendamento?: {
    titulo?: string;
    subtitulo?: string;
    detalhes?: string[];
  };
};

export type ConclusaoCheckinFluxo = {
  checkinExecucaoId: string;
  fluxo: Record<string, unknown>;
  status: "autorizado" | "pendente";
};

const client = axios.create({
  baseURL: `${process.env.REACT_APP_AXYMA_MICROSSERVICES_APIS_URL}/exec/public/fluxo`,
  // Necessário para que o navegador envie o cookie HttpOnly checkin_fluxo_sessao.
  withCredentials: true,
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
});

type RespostaPublica<T> = { success: boolean; data: T };
const extrairDados = <T>(resposta: RespostaPublica<T>) => resposta.data;

class CheckinFluxoPublico {
  iniciar = async (aliasEmpresa: string, aliasChecklist: string) => {
    const { data } = await client.get<RespostaPublica<SessaoCheckinFluxo>>(
      `/${encodeURIComponent(aliasEmpresa)}/${encodeURIComponent(aliasChecklist)}`,
    );
    return extrairDados(data);
  };

  abrir = async (
    aliasEmpresa: string,
    aliasChecklist: string,
    sessao: string,
    localizacao?: LocalizacaoPublica,
  ) => {
    const { data } = await client.post<RespostaPublica<AberturaCheckinFluxo>>(
      `/${encodeURIComponent(aliasEmpresa)}/${encodeURIComponent(aliasChecklist)}/abrir`,
      {
        ...(localizacao ? { localizacao } : {}),
        sessao,
      },
    );
    return extrairDados(data);
  };

  validar = async (
    aliasEmpresa: string,
    aliasChecklist: string,
    sessao: string,
    identificador: string,
  ) => {
    const { data } = await client.post<RespostaPublica<ValidacaoCheckinFluxo>>(
      `/${encodeURIComponent(aliasEmpresa)}/${encodeURIComponent(aliasChecklist)}/validar`,
      {
        identificador,
        sessao,
      },
    );
    return extrairDados(data);
  };

  continuarSemAgendamento = async (
    aliasEmpresa: string,
    aliasChecklist: string,
    sessao: string,
  ) => {
    const { data } = await client.post<RespostaPublica<{ autorizado: true }>>(
      `/${encodeURIComponent(aliasEmpresa)}/${encodeURIComponent(aliasChecklist)}/continuar-sem-agendamento`,
      { sessao },
    );
    return extrairDados(data);
  };

  concluir = async (
    aliasEmpresa: string,
    aliasChecklist: string,
    sessao: string,
    respostas: FormToFilling,
    localizacao: LocalizacaoPublica | undefined,
    inicioHistoricoAnonimo: number,
  ) => {
    const { data } = await client.post<RespostaPublica<ConclusaoCheckinFluxo>>(
      `/${encodeURIComponent(aliasEmpresa)}/${encodeURIComponent(aliasChecklist)}/concluir`,
      {
        // A API pública aceita somente a árvore de respostas; metadados usados
        // pelo preenchimento interno não devem vazar para esta jornada.
        respostas: { itens: respostas.itens },
        ...(localizacao ? { localizacao } : {}),
        sessao,
        inicioHistoricoAnonimo,
      },
    );
    return extrairDados(data);
  };
}

export default CheckinFluxoPublico;
